import { writable, get } from "svelte/store";
import analytics from '../services/analytics.js';
import { debounce } from '../utils/helpers.js';

/**
 * @typedef {Object} State
 * @property {string} query
 * @property {string|null} category
 */
const initialState = {
  query: "",
  category: null,
};

// Debounced function for search tracking to avoid too many events
const debouncedTrackSearch = debounce((query, resultCount) => {
  if (query && query.trim().length > 0) {
    analytics.trackSearch(query, resultCount);
  }
}, 1000); // 1 second debounce

/**
 * @typedef {Object} Store
 * @property {function((state: State) => void): void} subscribe - Subscribes to changes in the store, providing the current state to the callback.
 * @property {function(string): void} updateQuery - Updates the query state with a new string value.
 * @property {function(string|null): void} updateCategory - Updates the category state with a new string or null value.
 * @property {function(): void} reset - Resets the store to its initial state.
 * @property {function(): State} getState - Retrieves the current state of the store.
 * @property {function(number): void} trackSearchResults - Tracks search results count for analytics.
 */

/**
 * @type {import('svelte/store').Writable<State>}
 */
const state = writable(initialState);

/**
 * Creates a new store with query and category state
 *
 * @returns {Store} store
 */
function createStore() {
  const { subscribe, update, set } = state;

  return {
    subscribe,
    updateQuery: (newQuery) =>
      update((/** @type {State} **/ state) => {
        return { ...state, query: newQuery, category: null };
      }),
    updateCategory: (newCategory) =>
      update((/** @type {State} **/ state) => {
        return { ...state, category: newCategory, query: "" };
      }),
    reset: () => set(initialState),
    getState: () => {
      return get(state);
    },
    trackSearchResults: (resultCount) => {
      const currentState = get(state);
      debouncedTrackSearch(currentState.query, resultCount);
    }
  };
}

export const searchStore = createStore();
